import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const GallerySection = styled.section`
  width: 100%;
  padding: ${props => props.theme.spacing.mega} 0;
  background: ${props => props.theme.colors.background.primary};
`;

const GalleryContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.large};
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: ${props => props.theme.fontSizes.hero};
  margin-bottom: ${props => props.theme.spacing.medium};

  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.neon.cyan} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: ${props => props.theme.fontSizes.large};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.xxlarge};
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const SliderWrapper = styled.div`
  .slick-slide {
    padding: 0 ${props => props.theme.spacing.medium};
  }

  .slick-dots {
    bottom: -40px;

    li button:before {
      color: ${props => props.theme.colors.primary};
      font-size: 12px;
    }

    li.slick-active button:before {
      color: ${props => props.theme.colors.primary};
    }
  }

  .slick-arrow {
    z-index: 10;

    &:before {
      color: ${props => props.theme.colors.primary};
      font-size: 30px;
    }
  }

  .slick-prev {
    left: -40px;

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      left: 10px;
    }
  }

  .slick-next {
    right: -40px;

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      right: 10px;
    }
  }
`;

const ImageCard = styled.div`
  cursor: pointer;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.large};
  transition: all ${props => props.theme.transitions.medium};

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.glowStrong};

    img {
      transform: scale(1.05);
    }
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: ${props => props.theme.borderRadius.large};
  transition: transform ${props => props.theme.transitions.medium};
  display: block;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    height: 300px;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xxlarge};
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.fontSizes.large};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xxlarge};
  color: ${props => props.theme.colors.neon.pink};
  font-size: ${props => props.theme.fontSizes.large};
`;

const CTAButton = styled.a`
  display: inline-block;
  margin: ${props => props.theme.spacing.mega} auto 0;
  padding: ${props => props.theme.spacing.medium} ${props => props.theme.spacing.xlarge};
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.background.primary};
  text-decoration: none;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-weight: 600;
  font-size: ${props => props.theme.fontSizes.medium};
  transition: all ${props => props.theme.transitions.fast};
  box-shadow: ${props => props.theme.shadows.glow};
  text-align: center;
  display: block;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;

  &:hover {
    background: ${props => props.theme.colors.primaryLight};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.glowStrong};
  }
`;

interface ImageFromAPI {
  id: number;
  url: string;
  width: number;
  height: number;
}

const CanvasoftGallery: React.FC = () => {
  const [images, setImages] = useState<ImageFromAPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          'https://canvasoft.com:8487/canvamanager/imgs/latest-ten'
        );

        if (!response.ok) {
          throw new Error('Failed to fetch images');
        }

        const data = await response.json();
        setImages(data);
      } catch (err) {
        console.error('Error fetching Canvasoft images:', err);
        setError('Unable to load images at this time. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  const settings = {
    dots: true,
    infinite: images.length > 3,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  if (loading) {
    return (
      <GallerySection id="canvasoft-gallery">
        <GalleryContainer>
          <SectionTitle>Art created with NeuralPrompt</SectionTitle>
          <LoadingMessage>Loading amazing creations...</LoadingMessage>
        </GalleryContainer>
      </GallerySection>
    );
  }

  if (error) {
    return (
      <GallerySection id="canvasoft-gallery">
        <GalleryContainer>
          <SectionTitle>Art created with NeuralPrompt</SectionTitle>
          <ErrorMessage>{error}</ErrorMessage>
        </GalleryContainer>
      </GallerySection>
    );
  }

  const handleImageClick = (image: ImageFromAPI) => {
    window.open('https://canvasoft.com', '_blank');
  };

  return (
    <GallerySection id="canvasoft-gallery">
      <GalleryContainer>
        <SectionTitle>Art created with NeuralPrompt</SectionTitle>
        <SectionSubtitle>
          Latest masterpieces from the Canvasoft community, powered by NeuralPrompt prompts
        </SectionSubtitle>

        {images.length > 0 ? (
          <>
            <SliderWrapper>
              <Slider {...settings}>
                {images.map((image) => (
                  <div key={image.id}>
                    <ImageCard onClick={() => handleImageClick(image)}>
                      <GalleryImage
                        src={image.url}
                        alt="Canvasoft.com"
                        width={image.width}
                        height={image.height}
                        loading="lazy"
                      />
                    </ImageCard>
                  </div>
                ))}
              </Slider>
            </SliderWrapper>

            <CTAButton href="https://canvasoft.com" target="_blank" rel="noopener noreferrer">
              Visit Canvasoft Gallery
            </CTAButton>
          </>
        ) : (
          <LoadingMessage>No images available at this time.</LoadingMessage>
        )}
      </GalleryContainer>
    </GallerySection>
  );
};

export default CanvasoftGallery;
